import type {
  SubscriptionInfo,
  SubscriptionNoticeModalProps,
  UpgradePermissionsInfo,
} from 'redux/modules/subscriptions.types';
import type { UserPermissionsFacade } from 'redux/modules/user.types';
import {
  getAccountMemoryHistoryDays,
  getCurrentAccount,
  getSubscriptionFeaturesByPlan,
} from 'redux/selectors/accounts';
import { getIsUserAdmin, getUser, getUserPermissions } from 'redux/selectors/user';
import { getAccountOwner } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';

export const getSubscriptionModalMessageKey = (state: IState) => state.subscriptions.showMessageKey;
export const getAddOnScopeKey = (state: IState) => state.subscriptions.addOnScopeKey;

/** The React subscription settings page's data (`routes/subscription`). */
export const getSubscriptionPage = (state: IState) => state.subscriptions.page;
export const getSubscriptionPageSubscription = (state: IState) =>
  state.subscriptions.page.subscription;
export const getSubscriptionPageInvoices = (state: IState) => state.subscriptions.page.invoices;
export const getSubscriptionPagePlans = (state: IState) => state.subscriptions.page.plans;
export const getSubscriptionPagePlansFailed = (state: IState) =>
  state.subscriptions.page.plansFailed;

export const getSubscriptionInfo = createSelector(
  [
    getCurrentAccount,
    getIsUserAdmin,
    getAccountOwner,
    getSubscriptionModalMessageKey,
    getAccountMemoryHistoryDays,
  ],
  (account, isAdmin, owner, subscriptionMessageKey, memoryHistoryDays): SubscriptionInfo => {
    return {
      isAdmin,
      isTeam: account.num_users > 1,
      isInTrial: account.trial,
      isEnterprise: account.plan_custom,
      accountId: account.id,
      accountOwnerEmail: (owner && owner.email) || undefined,
      isPaid: account.paid,
      subscriptionMessageKey,
      memoryHistoryDays,
      features: account.features,
      planName: account.plan_name,
    };
  }
);

export const getCanSeeTasks = createSelector(
  [getSubscriptionInfo],
  ({ isEnterprise, isAdmin, features }) => {
    if (isEnterprise) {
      return features.tasks || isAdmin;
    }
    return true;
  }
);

export const upgradePermissionFactory = (
  subscriptionInfo: SubscriptionInfo,
  permissions: UserPermissionsFacade
): UpgradePermissionsInfo => {
  const { accountOwnerEmail, isInTrial, accountId, isPaid, isAdmin, subscriptionMessageKey } =
    subscriptionInfo;

  const canUserUpgradeSubscription = subscriptionInfo.isEnterprise
    ? permissions.canUpgradeSubscription
    : permissions.canManageSubscriptions;

  const isAdminButCantUpgrade =
    isAdmin && !canUserUpgradeSubscription && !subscriptionInfo.isEnterprise;

  const canContactWorkspaceOwner =
    accountOwnerEmail !== undefined && (permissions.canContactAdmin || isAdminButCantUpgrade);

  return {
    canUserUpgradeSubscription,
    canContactWorkspaceOwner,
    canContactAccountManager: permissions.canContactManager,
    shouldOpenExternalSalesPage: subscriptionMessageKey === 'azureADIntegrationRequiresUpgrade',
    shouldOpenPlansModal:
      canUserUpgradeSubscription &&
      isPaid &&
      !isInTrial &&
      subscriptionMessageKey !== 'userLimitReached',
    accountOwnerEmail,
    accountId,
  };
};

export const getUpgradePermissionsInfo = createSelector(
  [getSubscriptionInfo, getUserPermissions],
  (subscriptionInfo, userPermissions): UpgradePermissionsInfo =>
    upgradePermissionFactory(subscriptionInfo, userPermissions)
);

export const getSubscriptionNoticeModalProps = createSelector(
  [
    getSubscriptionModalMessageKey,
    getSubscriptionInfo,
    getUserPermissions,
    getSubscriptionFeaturesByPlan,
  ],
  (messageKey, subscriptionInfo, userPermissions, featuresByPlan): SubscriptionNoticeModalProps => {
    return {
      userPermissions,
      subscriptionInfo,
      messageKey,
      featuresByPlan,
    };
  }
);

export interface UpgradeContactInfo {
  ownerName: string;
  ownerEmail: string;
  userName: string;
}

export const getUpgradeContactInfo = createSelector(
  [getAccountOwner, getUser],
  (accountOwner, user): UpgradeContactInfo | undefined => {
    if (!accountOwner || !user) {
      return undefined;
    }

    return {
      ownerName: accountOwner.name,
      ownerEmail: accountOwner.email,
      userName: user.name,
    };
  }
);
